import React, { useMemo, useState } from "react";

const GOLD = "#f4c76c";
const GOLD_DEEP = "#d59a32";
const DARK = "#0b1020";
const BRAND = "#1f2937";

const PLAN_TABS = [
  { key: "basic", label: "Basic" },
  { key: "starter", label: "Starter" },
  { key: "professional", label: "Professional" },
];

const PRICING_CONFIG = {
  basic: {
    baseYear: 799,
    items: [
      { durationKey: "1m", label: "1 Month", amount: { legacy: 99, new: 149 } },
      { durationKey: "3m", label: "3 Month", amount: 249 },
      { durationKey: "6m", label: "6 Month", amount: 449 },
      { durationKey: "1y", label: "1 Year", amount: 799 },
      { durationKey: "3y", label: "3 Year", amount: 1999 },
    ],
  },
  starter: {
    baseYear: 1999,
    items: [
      { durationKey: "1m", label: "1 Month", amount: { legacy: 199, new: 299 } },
      { durationKey: "3m", label: "3 Month", amount: 549 },
      { durationKey: "6m", label: "6 Month", amount: 1800 },
      { durationKey: "1y", label: "1 Year", amount: 1999 },
      { durationKey: "3y", label: "3 Year", amount: 4999 },
    ],
  },
  professional: {
    baseYear: 5999,
    items: [
      { durationKey: "1y", label: "1 Year", amount: 5999 },
    ],
  },
};

const FEATURE_MATRIX_BASE = [
  { key: "addCore", basic: true, starter: true, professional: true },
  { key: "posQr", basic: true, starter: true, professional: true },
  { key: "fastBilling", basic: true, starter: true, professional: true },
  { key: "kitchenDisplay", basic: true, starter: true, professional: true },
  { key: "aiImages", basic: true, starter: true, professional: true },
  { key: "scanMenu", basic: true, starter: true, professional: true },
  { key: "staffRoles", basic: false, starter: true, professional: true },
  { key: "liveSync", basic: false, starter: true, professional: true },
  { key: "finance", basic: false, starter: true, professional: true },
  { key: "reports", basic: false, starter: true, professional: true },
  { key: "customWorkflow", basic: false, starter: true, professional: true },
  { key: "inventory", basic: false, starter: true, professional: true },
  { key: "billViaQr", basic: false, starter: true, professional: true },
  { key: "webAccess", basic: false, starter: false, professional: true },
];

const FEATURE_ICON_MAP = {
  unifiedPosQr: "✨",
  productImages: "🖼️",
  liveKot: "🔥",
  inventory: "📦",
  scanMenu: "📷",
  billViaQr: "🔳",
  liveSync: "🔁",
  fastBilling: "⚡",
  smartFinance: "📊",
  staffAudit: "🛡️",
  incomeExpense: "📒",
  reports: "📈",
  support: "🎧",
  platforms: "📱",
};

const COPY = {
  title: "Our Plans",
  premiumBadge: "Premium Experience",
  reliable: "Reliable, always-on",
  heroTitle: "Luxury-Grade Restaurant Management Software",
  heroBody:
    "Modern POS, QR dine-in, kitchen management, income/expense & reports — now with a Professional plan that includes Web access (without printer).",
  printerNote:
    "Printer add-on (optional): 2-inch ₹1999, 3-inch ₹3000. Add to any plan if needed.",
  demo: "Book a demo",
  call: "Schedule a call",
  pickLane: "Choose a Plan",
  featureComparison: "Feature comparison",
  featureComparisonSub: "Compare Basic, Starter, and Professional benefits",
  execution: "Execution toolkit",
  executionSub: "What your team gets on day one",
  customWorkflows: "Custom workflows",
  customBody:
    "Need franchise-ready rollouts, unique menu flows or integrations? We design it, ship updates after launch and train your floor staff.",
  requestCustomBuild: "Request a custom build",
  featureMatrix: {
    addCore: "Add products/categories/tables/orders",
    posQr: "POS + QR basics",
    fastBilling: "Fast billing shortcuts",
    kitchenDisplay: "Kitchen display + auto print",
    aiImages: "AI product images",
    scanMenu: "Scan menu with AI",
    staffRoles: "Staff roles (Owner/Cashier/Waiter/Chef)",
    liveSync: "Live sync (owner & staff)",
    finance: "Finance, income/expense & Khatabook",
    reports: "Day / month / waiter / table reports",
    customWorkflow: "Custom workflows & training",
    inventory: "Inventory",
    billViaQr: "Bill / order via QR codes",
    webAccess: "Web dashboard access (no printer)",
  },
  features: {
    unifiedPosQr: {
      title: "Unified POS + QR",
      subtitle: "POS, dine-in, takeaways and QR table orders on one lightning panel.",
    },
    productImages: {
      title: "Product images with AI",
      subtitle: "Instantly generate crisp food photos and keep your menu glossy.",
    },
    liveKot: {
      title: "Live KOT & Kitchen",
      subtitle: "Fire KOTs, track statuses, print tokens and avoid double-prep.",
    },
    inventory: {
      title: "Inventory + Costing",
      subtitle: "Real-time stock, recipe costs and variance reports in one tap.",
    },
    scanMenu: {
      title: "Scan menu with AI",
      subtitle: "Upload/scan menus and get dishes, prices and tags auto-created.",
    },
    billViaQr: {
      title: "Bill via QR codes",
      subtitle: "Guests scan, order and pay with table-wise QR codes.",
    },
    liveSync: {
      title: "Live sync for teams",
      subtitle: "Owner, cashier, chef and waiter stay synced in real time.",
    },
    fastBilling: {
      title: "Fast billing",
      subtitle: "3-tap quick bills with saved combos, offers and payment modes.",
    },
    smartFinance: {
      title: "Smart Finance",
      subtitle: "GST-ready billing, expenses and payment insights for owners.",
    },
    staffAudit: {
      title: "Staff & Audit",
      subtitle: "Roles, approvals, clock-ins and a clear log of who did what.",
    },
    incomeExpense: {
      title: "Income, expense & Khatabook",
      subtitle: "Track cashflows, credit, and ledger history without spreadsheets.",
    },
    reports: {
      title: "Reports that matter",
      subtitle: "Day/month summaries, waiter & table wise insights, exports.",
    },
    support: {
      title: "Support that ships",
      subtitle: "Playbook-driven onboarding with updates shipped after go-live.",
    },
    platforms: {
      title: "Android & iOS (web coming soon)",
      subtitle: "Run outlets on mobile today; web back-office arrives soon.",
    },
  },
};

const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const resolveAmount = (amountConfig) => {
  if (amountConfig && typeof amountConfig === "object") {
    return Number(amountConfig.new ?? amountConfig.legacy ?? 0);
  }
  return Number(amountConfig || 0);
};

const sendWhatsApp = (text) => {
  const phone = "9988777409";
  const msg = `Hi, I would like to know more about ${text}.`;
  const url = `https://wa.me/91${phone}?text=${encodeURIComponent(msg)}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

const callSales = () => {
  const phone = "+919988777409";
  window.location.href = `tel:${phone}`;
};

const PRINTER_OPTIONS = [
  { key: "app", label: "App Only"  },
  { key: "2in", label: "2-inch Printer", amount: 2000 },
  { key: "3in", label: "3-inch Printer", amount: 3000 },
];

const PaymentModal = ({ open, onClose, selectedPrice, onPhonePePay, phonePeBusy }) => {
  if (!open || !selectedPrice) return null;
  const { planName, durationLabel, amount, printerLabel, printerAmount } =
    selectedPrice;
  const totalAmount = Number(amount || 0) + Number(printerAmount || 0);
  return (
    <div style={sx.pmOverlay} onClick={onClose}>
      <div style={sx.pmSheet} onClick={(e) => e.stopPropagation()}>
        <div style={sx.pmHandle} />
        <div style={sx.pmHeader}>
          <div style={sx.pmIcon}>🔒</div>
          <div style={{ flex: 1 }}>
            <div style={sx.pmTitle}>Confirm & Pay</div>
            <div style={sx.pmSub}>You selected {planName} • {durationLabel}</div>
          </div>
          <button style={sx.pmClose} onClick={onClose}>✕</button>
        </div>

        <div style={sx.pmSummary}>
          <div style={{ flex: 1 }}>
            <div style={sx.pmPlan}>{planName} Plan</div>
            <div style={sx.pmMeta}>Secure support • Assisted setup</div>
            {printerLabel ? (
              <div style={sx.pmMeta}>Printer: {printerLabel}</div>
            ) : null}
          </div>
          <div style={sx.pmAmountWrap}>
            <div style={sx.pmAmountLabel}>Payable</div>
            <div style={sx.pmAmount}>{formatINR(totalAmount)}</div>
          </div>
        </div>

        <button
          style={{ ...sx.pmPrimaryBtn, marginTop: 12 }}
          onClick={() =>
            sendWhatsApp(
              `${planName} - ${durationLabel} (${formatINR(
                totalAmount
              )})${printerLabel ? ` + ${printerLabel}` : ""}`
            )
          }
        >
          💬 Pay / Confirm on WhatsApp
        </button>

        <button
          style={{ ...sx.pmPhonePeBtn, marginTop: 10 }}
          onClick={onPhonePePay}
          disabled={phonePeBusy}
        >
          {phonePeBusy ? "Starting PhonePe..." : "Pay with PhonePe"}
        </button>

        <div style={sx.pmBtnRow}>
          <button style={sx.pmSecondaryBtn} onClick={callSales}>📞 Call Sales</button>
          <button style={sx.pmGhostBtn} onClick={onClose}>Not Now</button>
        </div>
      </div>
    </div>
  );
};

const PlanShowcase = () => {
  const [activeTab, setActiveTab] = useState("starter");
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [printerOption, setPrinterOption] = useState("app");
  const [phonePeBusy, setPhonePeBusy] = useState(false);

  const featureMatrix = useMemo(
    () =>
      FEATURE_MATRIX_BASE.map((row) => ({
        ...row,
        title: COPY.featureMatrix[row.key] || row.key,
      })),
    []
  );

  const features = useMemo(
    () =>
      Object.entries(FEATURE_ICON_MAP).map(([key, icon]) => ({
        key,
        icon,
        title: COPY.features?.[key]?.title || key,
        subtitle: COPY.features?.[key]?.subtitle || "",
      })),
    []
  );

  const activePlanName =
    activeTab === "basic"
      ? "Basic"
      : activeTab === "starter"
      ? "Starter"
      : activeTab === "professional"
      ? "Professional"
      : "Starter";
  const pricingData = useMemo(() => {
    const plan = PRICING_CONFIG[activeTab];
    if (!plan) return { baseYear: 0, items: [] };
    return {
      baseYear: Number(plan.baseYear || 0),
      items: (plan.items || []).map((row) => ({
        ...row,
        amount: resolveAmount(row.amount),
      })),
    };
  }, [activeTab]);

  const onSelectPrice = (row) => {
    setSelectedPrice({
      planKey: activeTab,
      planName: activePlanName,
      durationKey: row.durationKey,
      durationLabel: row.label,
      amount: row.amount,
    });
  };

  const isPhonePeSuccess = (result) => {
    const raw =
      result?.response?.state ||
      result?.response?.code ||
      result?.response?.paymentStatus ||
      result?.response?.status ||
      result?.response?.data?.state ||
      result?.response?.data?.code ||
      result?.response?.data?.paymentStatus ||
      result?.response?.data?.status ||
      "";
    const status = String(raw).trim().toUpperCase();
    return (
      status === "COMPLETED" ||
      status === "SUCCESS" ||
      status === "PAYMENT_SUCCESS"
    );
  };

  const checkPhonePeStatus = async (merchantOrderId) => {
    if (!merchantOrderId) return;
    try {
      const response = await fetch("https://qr.billsarthi.in/public/api/phonepe/status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ merchant_order_id: merchantOrderId }),
      });
      const result = await response.json();
      const state = result?.response?.state || result?.response?.code || "UNKNOWN";
      if (isPhonePeSuccess(result)) {
        alert(`Payment status: ${state}`);
      } else {
        alert(`Payment status: ${state}`);
      }
    } catch (error) {
      alert(error?.message || "Failed to fetch status.");
    }
  };

  const onPhonePePay = async () => {
    if (!selectedPrice || phonePeBusy) return;
    setPhonePeBusy(true);
    try {
      const stored = localStorage.getItem("userData");
      const parsed = stored ? JSON.parse(stored) : {};
      const data = parsed?.data || parsed || {};
      const restaurantid = data?.id;
      const userId = data?.user_id || data?.id || null;
      const mobileNumber = data?.mobile_number || "";

      const printer =
        PRINTER_OPTIONS.find((p) => p.key === printerOption) ||
        PRINTER_OPTIONS[0];
      const totalAmount =
        Number(selectedPrice.amount || 0) + Number(printer.amount || 0);

      const payload = {
        amount: totalAmount,
        plan_key: selectedPrice.planKey,
        duration_key: selectedPrice.durationKey,
        restaurantid,
        user_id: userId,
        mobile_number: mobileNumber,
        device_os: "WEB",
        printer_option: printer.key,
        printer_amount: printer.amount,
      };

      const response = await fetch(
        "https://qr.billsarthi.in/public/api/phonepe/initiate",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const result = await response.json();
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "PhonePe initiate failed");
      }

      const merchantOrderId =
        result?.merchantOrderId ||
        result?.merchant_order_id ||
        result?.data?.merchantOrderId ||
        result?.data?.merchant_order_id;
      const redirectUrl =
        result?.redirectUrl ||
        result?.paymentUrl ||
        result?.payment_url ||
        result?.data?.redirectUrl ||
        result?.data?.redirect_url ||
        result?.data?.paymentUrl ||
        result?.data?.payment_url ||
        result?.data?.instrumentResponse?.redirectInfo?.url ||
        result?.response?.data?.instrumentResponse?.redirectInfo?.url;

      setPayModalOpen(false);

      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        console.log("PhonePe initiate response (no redirect url):", result);
        alert("Payment link missing. Please contact support.");
        await checkPhonePeStatus(merchantOrderId);
      }
    } catch (error) {
      alert(error?.message || "Payment error");
    } finally {
      setPhonePeBusy(false);
    }
  };

  return (
    <div style={sx.page}>
      <div style={sx.glowOne} />
      <div style={sx.glowTwo} />

      <div style={sx.container}>
        <header style={sx.header}>
          <div style={sx.headerBadge}>🏅</div>
          <div>
            <div style={sx.headerTitle}>{COPY.title}</div>
            <div style={sx.headerSub}>Plans built for modern restaurants</div>
          </div>
        </header>

        <section style={sx.hero}>
          <div style={sx.heroTop}>
            <div style={sx.heroBadge}>✨ {COPY.premiumBadge}</div>
            <div style={sx.heroPill}>🛡️ {COPY.reliable}</div>
          </div>
          <div style={sx.heroTitle}>{COPY.heroTitle}</div>
          <div style={sx.heroBody}>{COPY.heroBody}</div>
          <div style={sx.heroNote}>{COPY.printerNote}</div>
          <div style={sx.heroActions}>
            <button style={sx.primaryCta} onClick={() => sendWhatsApp(COPY.demo)}>
              💬 {COPY.demo}
            </button>
            <button style={sx.secondaryCta} onClick={callSales}>📞 {COPY.call}</button>
          </div>
        </section>

        <section style={sx.section}>
          <div style={sx.sectionHeader}>
            <div style={sx.sectionTitle}>{COPY.pickLane}</div>
            <div style={sx.sectionSub}>Tap a plan, then select duration to continue.</div>
          </div>

          <div style={sx.tabsWrap}>
            {PLAN_TABS.map((tab) => {
              const active = tab.key === activeTab;
              return (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setSelectedPrice(null);
                  }}
                  style={{
                    ...sx.tabPill,
                    ...(active ? sx.tabPillActive : null),
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div style={sx.pricingCard}>
          <div style={sx.pricingTop}>
            <div>
              <div style={sx.pricingPlan}>{activePlanName} Plan</div>
              <div style={sx.pricingBase}>
                1 Year Base Price: <strong>{formatINR(pricingData.baseYear)}</strong>
              </div>
            </div>
            {/* <div style={sx.bestValue}>Best Value</div> */}
          </div>

          <div style={sx.pricingHead}>
            <span>Duration</span>
            <span>Standard Price</span>
          </div>

          <div style={sx.pricingRows}>
            {pricingData.items.map((row) => {
              const isSelected =
                selectedPrice?.planKey === activeTab &&
                selectedPrice?.durationKey === row.durationKey;
              return (
                <button
                  key={row.durationKey}
                  onClick={() => onSelectPrice(row)}
                  style={{
                    ...sx.priceRow,
                    ...(isSelected ? sx.priceRowSelected : null),
                  }}
                >
                  <div style={sx.priceLeft}>
                    <span style={sx.radio}>{isSelected ? "●" : "○"}</span>
                    <span style={sx.priceDuration}>{row.label}</span>
                    {row.durationKey === "3y" ? (
                      <span style={sx.chipBest}>BEST VALUE</span>
                    ) : row.durationKey === "1y" ? (
                      <span style={sx.chipPopular}>POPULAR</span>
                    ) : null}
                  </div>
                  <div style={sx.priceRight}>
                    <span style={sx.priceAmount}>{formatINR(row.amount)}</span>
                    <span style={sx.chev}>›</span>
                  </div>
                </button>
              );
            })}
          </div>
          </div>

          <div style={sx.printerCard}>
            <div style={sx.printerTitle}>Printer Option</div>
            <div style={sx.printerSub}>
              Select if you want a printer with this plan.
            </div>
            {!selectedPrice && (
              <div style={sx.printerHint}>Pick a duration to continue.</div>
            )}
            <div style={sx.printerOptions}>
              {PRINTER_OPTIONS.map((opt) => {
                const active = printerOption === opt.key;
                return (
                  <button
                    key={opt.key}
                    style={{
                      ...sx.printerOption,
                      ...(active ? sx.printerOptionActive : null),
                    }}
                    onClick={() => setPrinterOption(opt.key)}
                  >
                    <span>{opt.label}</span>
                    <span style={sx.printerPrice}>
                      {opt.key === "app"
                        ? selectedPrice?.amount
                          ? formatINR(selectedPrice.amount)
                          : "Select plan"
                        : formatINR(opt.amount)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section style={sx.section}>
          <div style={sx.sectionHeader}>
            <div style={sx.sectionTitle}>{COPY.featureComparison}</div>
            <div style={sx.sectionSub}>{COPY.featureComparisonSub}</div>
          </div>

          <div style={sx.compareCard}>
            {featureMatrix.map((row) => (
              <div key={row.title} style={sx.compareRow}>
                <div style={sx.compareLabel}>✨ {row.title}</div>
                <div style={sx.compareBadges}>
                  <span style={{ ...sx.compareBadge, ...(row.basic ? sx.badgeOn : sx.badgeOff) }}>
                    {row.basic ? "✔" : "✕"} Basic
                  </span>
                  <span style={{ ...sx.compareBadge, ...(row.starter ? sx.badgeOnDark : sx.badgeOff) }}>
                    {row.starter ? "✔" : "✕"} Starter
                  </span>
                  <span style={{ ...sx.compareBadge, ...(row.professional ? sx.badgeOnGold : sx.badgeOff) }}>
                    {row.professional ? "✔" : "✕"} Professional
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section style={sx.section}>
          <div style={sx.sectionHeader}>
            <div style={sx.sectionTitle}>{COPY.execution}</div>
            <div style={sx.sectionSub}>{COPY.executionSub}</div>
          </div>

          <div style={sx.featuresGrid}>
            {features.map((feature) => (
              <div key={feature.key} style={sx.featureCard}>
                <div style={sx.featureIcon}>{feature.icon}</div>
                <div style={sx.featureTitle}>{feature.title}</div>
                <div style={sx.featureSub}>{feature.subtitle}</div>
              </div>
            ))}
          </div>

          <div style={sx.customCard}>
            <div style={sx.customHeader}>🛠️ {COPY.customWorkflows}</div>
            <div style={sx.customBody}>{COPY.customBody}</div>
            <button style={sx.customBtn} onClick={() => sendWhatsApp(COPY.customWorkflows)}>
              {COPY.requestCustomBuild} →
            </button>
          </div>
        </section>
      </div>

      {selectedPrice ? (
        <div style={sx.payBarWrap}>
          <div style={sx.payBar}>
            <div>
              <div style={sx.payBarTop}>
                {selectedPrice.planName} • {selectedPrice.durationLabel}
              </div>
              <div style={sx.payBarAmount}>
                {formatINR(
                  Number(selectedPrice.amount || 0) +
                    Number(
                      PRINTER_OPTIONS.find((p) => p.key === printerOption)
                        ?.amount || 0
                    )
                )}
              </div>
              <div style={sx.payBarMeta}>
                Printer:{" "}
                {PRINTER_OPTIONS.find((p) => p.key === printerOption)?.label ||
                  "App Only"}
              </div>
            </div>
            <div style={sx.payActions}>
              <button
                style={sx.clearBtn}
                onClick={() => {
                  setSelectedPrice(null);
                  setPrinterOption("app");
                }}
                title="Clear selection"
              >
                ⟲
              </button>
              <button
                style={sx.payBtn}
                onClick={() => {
                  const printer =
                    PRINTER_OPTIONS.find((p) => p.key === printerOption) ||
                    PRINTER_OPTIONS[0];
                  setSelectedPrice((prev) =>
                    prev
                      ? {
                          ...prev,
                          printerLabel: printer.label,
                          printerAmount: printer.amount,
                        }
                      : prev
                  );
                  setPayModalOpen(true);
                }}
              >
               Pay Now
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <PaymentModal
        open={payModalOpen}
        onClose={() => setPayModalOpen(false)}
        selectedPrice={selectedPrice}
        onPhonePePay={onPhonePePay}
        phonePeBusy={phonePeBusy}
      />
    </div>
  );
};

const sx = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(900px 420px at 0% -10%, rgba(96,165,250,0.18), transparent 60%), radial-gradient(800px 380px at 100% 0%, rgba(52,211,153,0.18), transparent 55%), #f6f7fb",
    color: "#0f172a",
    fontFamily: "Sora, \"Space Grotesk\", \"DM Sans\", system-ui, sans-serif",
    padding: 16,
    position: "relative",
    overflow: "hidden",
  },
  container: {
    width: "min(1100px, 100%)",
    margin: "0 auto",
  },
  glowOne: {
    position: "absolute",
    top: -80,
    right: -60,
    width: 220,
    height: 220,
    background: "#60a5fa",
    opacity: 0.14,
    borderRadius: 160,
  },
  glowTwo: {
    position: "absolute",
    bottom: -80,
    left: -60,
    width: 260,
    height: 260,
    background: "#34d399",
    opacity: 0.14,
    borderRadius: 200,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 14,
  },
  headerBadge: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: GOLD,
    display: "grid",
    placeItems: "center",
    color: DARK,
    fontWeight: 800,
  },
  headerTitle: { fontSize: 18, fontWeight: 900 },
  headerSub: { fontSize: 12, color: "#64748b" },

  hero: {
    borderRadius: 24,
    padding: 20,
    marginBottom: 18,
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(255,255,255,0.92))",
    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow: "0 22px 44px rgba(15,23,42,.10)",
  },
  heroTop: { display: "flex", gap: 10, flexWrap: "wrap" },
  heroBadge: {
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})`,
    color: DARK,
    padding: "6px 10px",
    borderRadius: 999,
    fontWeight: 800,
    fontSize: 12,
  },
  heroPill: {
    border: "1px solid rgba(15,23,42,0.12)",
    padding: "6px 10px",
    borderRadius: 999,
    color: "#0f172a",
    fontSize: 12,
    background: "#f1f5f9",
  },
  heroTitle: { fontSize: 24, fontWeight: 900, marginTop: 14 },
  heroBody: { color: "#475569", marginTop: 8, lineHeight: 1.5 },
  heroNote: {
    marginTop: 10,
    padding: "8px 10px",
    borderRadius: 12,
    background: "#f8fafc",
    border: "1px dashed rgba(15,23,42,.15)",
    color: "#334155",
    fontSize: 12,
    fontWeight: 700,
  },
  heroActions: { display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" },
  primaryCta: {
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})`,
    border: "none",
    color: DARK,
    fontWeight: 800,
    padding: "10px 14px",
    borderRadius: 12,
    cursor: "pointer",
  },
  secondaryCta: {
    background: "#fff",
    border: "1px solid rgba(15,23,42,0.12)",
    color: "#0f172a",
    fontWeight: 700,
    padding: "10px 14px",
    borderRadius: 12,
    cursor: "pointer",
  },

  section: { marginTop: 18 },
  sectionHeader: { marginBottom: 10 },
  sectionTitle: { fontSize: 17, fontWeight: 900, color: "#0f172a" },
  sectionSub: { fontSize: 12, color: "#64748b" },

  tabsWrap: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0,1fr))",
    gap: 10,
  },
  tabPill: {
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)",
    background: "#fff",
    color: "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  tabPillActive: {
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})`,
    color: DARK,
    border: "none",
  },

  pricingCard: {
    marginTop: 12,
    padding: 16,
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#ffffff",
    boxShadow: "0 18px 36px rgba(15,23,42,.10)",
  },
  printerCard: {
    marginTop: 12,
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#ffffff",
    boxShadow: "0 12px 26px rgba(15,23,42,.08)",
  },
  printerTitle: { fontWeight: 900, fontSize: 14 },
  printerSub: { color: "#64748b", fontSize: 12, marginTop: 4 },
  printerHint: {
    marginTop: 6,
    fontSize: 12,
    color: "#475569",
    fontWeight: 700,
  },
  printerOptions: {
    marginTop: 10,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 8,
  },
  printerOption: {
    border: "1px solid rgba(15,23,42,0.12)",
    background: "#f8fafc",
    borderRadius: 12,
    padding: "10px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontWeight: 700,
    cursor: "pointer",
  },
  printerOptionActive: {
    background: "rgba(244,199,108,0.18)",
    borderColor: GOLD,
  },
  printerPrice: { fontWeight: 800, color: "#0f172a" },
  pricingTop: { display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" },
  pricingPlan: { fontSize: 16, fontWeight: 800 },
  pricingBase: { fontSize: 12, color: "#64748b", marginTop: 4 },
  bestValue: {
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})`,
    color: DARK,
    padding: "6px 10px",
    borderRadius: 999,
    fontWeight: 800,
    fontSize: 12,
  },
  pricingHead: {
    display: "flex",
    justifyContent: "space-between",
    color: "#64748b",
    fontSize: 12,
    marginTop: 14,
  },
  pricingRows: { marginTop: 8, display: "grid", gap: 8 },
  priceRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 12,
    padding: "10px 12px",
    cursor: "pointer",
    color: "#0f172a",
  },
  priceRowSelected: {
    border: `1px solid ${GOLD}`,
    background: "rgba(244,199,108,0.18)",
    color: "#0f172a",
  },
  priceLeft: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  radio: { color: GOLD, fontWeight: 900 },
  priceDuration: { fontWeight: 700 },
  priceRight: { display: "flex", alignItems: "center", gap: 8 },
  priceAmount: { fontWeight: 800, color: "#0f172a" },
  chev: { color: "#94a3b8" },
  chipPopular: {
    background: "rgba(148,163,184,0.25)",
    color: "#475569",
    padding: "2px 6px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 800,
  },
  chipBest: {
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})`,
    color: DARK,
    padding: "2px 6px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 800,
  },

  compareCard: {
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#ffffff",
    boxShadow: "0 14px 30px rgba(15,23,42,.06)",
  },
  compareRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
    padding: "8px 0",
    borderBottom: "1px solid rgba(15,23,42,0.08)",
  },
  compareLabel: { fontWeight: 600, color: "#0f172a" },
  compareBadges: { display: "flex", gap: 6, flexWrap: "wrap" },
  compareBadge: {
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
  },
  badgeOn: { background: "rgba(244,199,108,0.15)", color: GOLD },
  badgeOnDark: { background: GOLD, color: DARK },
  badgeOnGold: { background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})`, color: DARK },
  badgeOff: { background: "rgba(148,163,184,0.2)", color: "#64748b" },

  featuresGrid: {
    marginTop: 10,
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  },
  featureCard: {
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#ffffff",
    boxShadow: "0 10px 22px rgba(15,23,42,.06)",
  },
  featureIcon: {
    width: 36,
    height: 36,
    borderRadius: 12,
    background: "rgba(244,199,108,0.15)",
    display: "grid",
    placeItems: "center",
    fontSize: 18,
  },
  featureTitle: { fontWeight: 800, marginTop: 8, color: "#0f172a" },
  featureSub: { color: "#64748b", fontSize: 12, marginTop: 4 },

  customCard: {
    marginTop: 14,
    padding: 16,
    borderRadius: 18,
    background: "linear-gradient(135deg, rgba(244,199,108,0.24), rgba(244,199,108,0.08))",
    border: "1px solid rgba(244,199,108,0.25)",
  },
  customHeader: { fontWeight: 900, color: GOLD_DEEP },
  customBody: { color: "#6b4b12", marginTop: 6, fontSize: 13 },
  customBtn: {
    marginTop: 12,
    background: GOLD,
    color: DARK,
    border: "none",
    borderRadius: 12,
    padding: "10px 12px",
    fontWeight: 800,
    cursor: "pointer",
  },

  payBarWrap: {
    position: "fixed",
    bottom: 16,
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "center",
    padding: "0 16px",
    zIndex: 50,
  },
  payBar: {
    width: "min(920px, 100%)",
    background: "#0f172a",
    border: "1px solid rgba(15,23,42,0.2)",
    borderRadius: 16,
    padding: "10px 14px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  payBarTop: { color: "#cbd5f5", fontSize: 12 },
  payBarMeta: { color: "#94a3b8", fontSize: 12, marginTop: 2 },
  payBarAmount: { fontSize: 18, fontWeight: 800 },
  payActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    justifyContent: "center",
  },
  clearBtn: {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.2)",
    background: "rgba(255,255,255,0.08)",
    color: "#e2e8f0",
    cursor: "pointer",
    fontWeight: 800,
  },
  payBtn: {
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})`,
    border: "none",
    color: DARK,
    padding: "10px 14px",
    borderRadius: 12,
    fontWeight: 900,
    cursor: "pointer",
  },

  pmOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(2,6,23,0.55)",
    display: "grid",
    placeItems: "center",
    padding: 16,
    zIndex: 60,
  },
  pmSheet: {
    width: "min(560px, 100%)",
    background: "#ffffff",
    borderRadius: 20,
    border: "1px solid rgba(15,23,42,0.12)",
    padding: 18,
    boxShadow: "0 22px 50px rgba(15,23,42,.22)",
  },
  pmHandle: {
    width: 44,
    height: 5,
    borderRadius: 999,
    background: "rgba(15,23,42,0.12)",
    margin: "0 auto 10px",
  },
  pmHeader: { display: "flex", gap: 10, alignItems: "center" },
  pmIcon: {
    width: 32,
    height: 32,
    borderRadius: 10,
    background: "rgba(244,199,108,0.25)",
    display: "grid",
    placeItems: "center",
  },
  pmTitle: { fontWeight: 800, color: "#0f172a" },
  pmSub: { color: "#64748b", fontSize: 12 },
  pmClose: {
    background: "#f8fafc",
    border: "1px solid rgba(15,23,42,0.12)",
    color: "#0f172a",
    borderRadius: 10,
    width: 30,
    height: 30,
    cursor: "pointer",
  },
  pmSummary: {
    marginTop: 12,
    padding: 12,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)",
    background: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
  },
  pmPlan: { fontWeight: 800, color: "#0f172a" },
  pmMeta: { color: "#64748b", fontSize: 12 },
  pmAmountWrap: { textAlign: "right" },
  pmAmountLabel: { color: "#64748b", fontSize: 12 },
  pmAmount: { fontSize: 20, fontWeight: 900, color: "#0f172a" },
  pmPrimaryBtn: {
    width: "100%",
    border: "none",
    borderRadius: 12,
    padding: "12px 14px",
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})`,
    color: DARK,
    fontWeight: 900,
    cursor: "pointer",
  },
  pmPhonePeBtn: {
    width: "100%",
    border: "1px solid rgba(15,23,42,0.12)",
    borderRadius: 12,
    padding: "12px 14px",
    background: "#fff",
    color: "#0f172a",
    fontWeight: 900,
    cursor: "pointer",
  },
  pmBtnRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12 },
  pmSecondaryBtn: {
    border: "1px solid rgba(15,23,42,0.12)",
    background: "#fff",
    color: "#0f172a",
    borderRadius: 12,
    padding: "10px",
    fontWeight: 700,
    cursor: "pointer",
  },
  pmGhostBtn: {
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#f8fafc",
    color: "#64748b",
    borderRadius: 12,
    padding: "10px",
    fontWeight: 700,
    cursor: "pointer",
  },
};

if (!document.getElementById("plan-showcase-styles")) {
  const styleTag = document.createElement("style");
  styleTag.id = "plan-showcase-styles";
  styleTag.innerHTML = `
    @media (max-width: 720px){
      .plan-paybar-hidden{ display:none; }
    }
  `;
  document.head.appendChild(styleTag);
}

export default PlanShowcase;
